import React from 'react';
import { HelpCircle } from 'lucide-react';

/* Same USD short-format as KpiStrip — local to keep Client Management list cards independent */

const fmtUSD = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−$' : '$';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export interface ClientDetailRevenueCardProps {
  /** USD — used for % bar math and, when no detail, as the main line */
  invoiced: number;
  outstanding: number;
  /** Native / base — when set, shown large; USD moves to small gray line */
  invoicedDetail?: string;
  outstandingDetail?: string;
  title?: string;
}

/** Compact horizontal layout — Client Detail / Invoice tab only */
export const ClientDetailRevenueOverviewCard: React.FC<ClientDetailRevenueCardProps> = ({
  invoiced,
  outstanding,
  invoicedDetail,
  outstandingDetail,
  title = 'Revenue Overview',
}) => {
  const outPct = invoiced > 0 ? (outstanding / invoiced) * 100 : 0;
  const collectedPct = Math.max(0, 100 - outPct);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex w-full h-full min-w-0 overflow-hidden">
      <div className="w-1 flex-none self-stretch bg-blue-500" />

      <div className="flex-1 min-w-0 px-3 py-2.5 flex flex-col gap-1.5">
        {/* Card title — full width */}
        <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
          {title}
        </p>

        {/* Two columns — always side by side */}
        <div className="flex items-start gap-0">
          {/* Total Invoiced */}
          <div className="flex-1 min-w-0 pr-3 border-r border-slate-100">
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Total Invoiced</p>
            <p className="text-base font-bold text-slate-900 tabular-nums leading-tight">
              {invoicedDetail ?? fmtUSD(invoiced)}
            </p>
            {invoicedDetail && (
              <p className="text-[10px] text-slate-400 tabular-nums mt-0.5">{fmtUSD(invoiced)}</p>
            )}
          </div>

          {/* Outstanding */}
          <div className="flex-1 min-w-0 pl-3">
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Outstanding (AR)</p>
            <p className="text-base font-bold text-amber-600 tabular-nums leading-tight">
              {outstandingDetail ?? fmtUSD(outstanding)}
            </p>
            {outstandingDetail && (
              <p className="text-[10px] text-slate-400 tabular-nums mt-0.5">{fmtUSD(outstanding)}</p>
            )}
            <span className={`mt-1 inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full tabular-nums
              ${collectedPct >= 90 ? 'bg-emerald-50 text-emerald-600' : collectedPct >= 75 ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
              {collectedPct.toFixed(1)}% collected
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface ClientDetailTimesheetHoursOverviewCardProps {
  totalHrs: number;
  billableHrs: number;
  nonBillableHrs: number;
  /** Billable ÷ total × 100 — shown under total and should match the bar. */
  utilizationPct: number;
  /** Card heading — same role as “Revenue Overview”. */
  title?: string;
  /** Optional tooltip + info icon on “Total hours” label. */
  totalHrsHint?: string;
  /** Narrow column: vertical stack, tighter type (pair with revenue card in a 2-col row). */
  compact?: boolean;
}

/**
 * Same 3-column pattern as ClientDetailRevenueOverviewCard: primary total,
 * billable vs non-bill progress bar, non-billable block + % of total badge.
 * Use compact for half-width layout.
 */
export const ClientDetailTimesheetHoursOverviewCard: React.FC<
  ClientDetailTimesheetHoursOverviewCardProps
> = ({
  totalHrs,
  billableHrs,
  nonBillableHrs,
  utilizationPct,
  title = 'Period hours',
  totalHrsHint,
  compact = false,
}) => {
  const fmtHrs = (n: number) =>
    n.toLocaleString(undefined, { maximumFractionDigits: 1 });

  const billablePct =
    totalHrs > 0 ? (billableHrs / totalHrs) * 100 : 0;
  const nonBillPct =
    totalHrs > 0 ? (nonBillableHrs / totalHrs) * 100 : 0;

  const barBlock = (
    <>
      <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-500 transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, billablePct)).toFixed(1)}%` }}
        />
      </div>
      <div className="flex justify-between gap-2 mt-0.5">
        <span className={`tabular-nums text-slate-600 ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
          {billablePct.toFixed(0)}% billable
        </span>
        <span className={`tabular-nums text-slate-600 ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
          {nonBillPct.toFixed(0)}% non-bill
        </span>
      </div>
    </>
  );

  const pctOfTotalBadge = (compactSize: boolean) =>
    nonBillableHrs > 0 &&
    totalHrs > 0 && (
      <span
        className={`
          shrink-0 font-semibold px-1.5 py-0.5 rounded-full tabular-nums
          ${compactSize ? 'text-[9px]' : 'text-[10px]'}
          ${nonBillPct > 25 ? 'bg-red-50 text-red-600' : nonBillPct > 10 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}
        `}
      >
        {nonBillPct.toFixed(1)}% of total
      </span>
    );

  /** Billable + non-billable — same hierarchy as former “Non-billable hours” block. */
  const billableNonBillHighlight = (compactSize: boolean) => {
    const labelCls = compactSize ? 'text-[9px]' : 'text-[10px]';
    const valueCls = compactSize ? 'text-xs' : 'text-sm';
    return (
      <div
        className={`grid gap-2 ${compactSize ? 'grid-cols-2 mt-1.5' : 'grid-cols-2 gap-4 mt-2'}`}
      >
        <div className="min-w-0">
          <p className={`text-slate-500 font-medium ${labelCls}`}>Billable hours</p>
          <p
            className={`font-bold tabular-nums leading-tight text-emerald-700 ${valueCls}`}
          >
            {fmtHrs(billableHrs)}
          </p>
        </div>
        <div className="min-w-0 flex flex-wrap items-end justify-between gap-1">
          <div>
            <p className={`text-slate-500 font-medium ${labelCls}`}>Non-billable hours</p>
            <p
              className={`font-bold tabular-nums leading-tight text-amber-600 ${valueCls}`}
            >
              {fmtHrs(nonBillableHrs)}
            </p>
          </div>
          {pctOfTotalBadge(compactSize)}
        </div>
      </div>
    );
  };

  if (compact) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-md flex w-full min-w-0 h-full overflow-hidden">
        <div className="w-1 flex-none self-stretch bg-blue-500" />
        <div className="flex-1 min-w-0 py-2 px-2.5 flex flex-col gap-2">
          <div>
            <p className="text-[8px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
              {title}
            </p>
            <p className="text-[9px] text-slate-500 font-medium flex items-center gap-0.5">
              Total hours
              {totalHrsHint && (
                <span className="inline-flex text-slate-400 cursor-help" title={totalHrsHint}>
                  <HelpCircle className="w-2.5 h-2.5" aria-hidden />
                </span>
              )}
            </p>
            <p className="text-base font-bold text-[#0D0D0D] tabular-nums leading-tight">
              {fmtHrs(totalHrs)}
            </p>
            {billableNonBillHighlight(true)}
            <p className="text-[9px] text-slate-600 tabular-nums leading-tight mt-1">
              {utilizationPct.toFixed(1)}% utilization
            </p>
          </div>
          <div className="border-t border-slate-100 pt-2">{barBlock}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex w-full min-w-0 flex-1 overflow-hidden">
      <div className="w-1 flex-none self-stretch bg-blue-500" />

      <div className="flex-1 min-w-0 py-2 pl-3 pr-3 flex flex-col gap-2.5 sm:flex-row sm:items-stretch sm:gap-0">
        <div className="flex-1 min-w-0 sm:pr-4 sm:border-r border-slate-100">
          <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
            {title}
          </p>
          <p className="text-[10px] text-slate-500 font-medium flex items-center gap-0.5">
            Total hours
            {totalHrsHint && (
              <span
                className="inline-flex text-slate-400 cursor-help align-middle"
                title={totalHrsHint}
              >
                <HelpCircle className="w-3 h-3" aria-hidden />
              </span>
            )}
          </p>
          <p className="text-lg font-bold text-[#0D0D0D] tabular-nums leading-tight">
            {fmtHrs(totalHrs)}
          </p>
          {billableNonBillHighlight(false)}
          <p className="text-[10px] text-slate-600 tabular-nums leading-tight mt-1.5">
            {utilizationPct.toFixed(1)}% utilization
          </p>
        </div>

        <div className="flex-1 min-w-[8rem] sm:px-4 flex flex-col justify-center border-t sm:border-t-0 border-slate-100 pt-2 sm:pt-0">
          {barBlock}
        </div>

        <div className="flex flex-col justify-center items-end sm:pl-4 sm:border-l border-slate-100 border-t sm:border-t-0 pt-2 sm:pt-0 min-w-[5.5rem]">
          {pctOfTotalBadge(false)}
        </div>
      </div>
    </div>
  );
};

const fmtUsd0 = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export interface ClientDetailTimesheetRevenueMetricsCardProps {
  hasBilled: boolean;
  billedValue: number;
  effectiveRate: number | null;
  totalLaborCost: number | null;
  costOfNonBillable: number | null;
  profit: number | null;
  marginPerBillableHour: number | null;
  title?: string;
  billedHint?: string;
  effectiveRateHint?: string;
  expensesHint?: string;
  /** Defaults to USD whole amounts + $/hr. */
  formatMoney0?: (n: number) => string;
  formatPerHour?: (n: number) => string;
}

/** Compact revenue / cost grid — matches timesheet hours card shell (no team·entries). */
export const ClientDetailTimesheetRevenueMetricsCard: React.FC<
  ClientDetailTimesheetRevenueMetricsCardProps
> = ({
  hasBilled,
  billedValue,
  effectiveRate,
  totalLaborCost,
  costOfNonBillable,
  profit,
  marginPerBillableHour,
  title = 'Revenue & margin (USD)',
  billedHint,
  effectiveRateHint,
  expensesHint,
  formatMoney0 = fmtUsd0,
  formatPerHour = (n) => `$${n.toFixed(2)}/hr`,
}) => {
  const cell = (
    label: React.ReactNode,
    value: React.ReactNode,
    valueClass: string,
  ) => (
    <div className="min-w-0">
      <div className="text-[9px] text-slate-500 font-medium leading-tight">{label}</div>
      <p className={`text-xs font-bold tabular-nums leading-tight mt-0.5 ${valueClass}`}>
        {value}
      </p>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex w-full min-w-0 h-full overflow-hidden">
      <div className="w-1 flex-none self-stretch bg-blue-500" />
      <div className="flex-1 min-w-0 py-2 px-2.5 flex flex-col">
        <p className="text-[8px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
          {title}
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 flex-1 content-start">
          {cell(
            <span className="inline-flex items-center gap-0.5">
              Total billed
              {billedHint && (
                <span className="text-slate-400 cursor-help" title={billedHint}>
                  <HelpCircle className="w-2.5 h-2.5 shrink-0" aria-hidden />
                </span>
              )}
            </span>,
            hasBilled ? formatMoney0(billedValue) : '—',
            'text-emerald-700',
          )}
          {cell(
            <span className="inline-flex items-center gap-0.5">
              Effective rate
              {effectiveRateHint && (
                <span className="text-slate-400 cursor-help" title={effectiveRateHint}>
                  <HelpCircle className="w-2.5 h-2.5 shrink-0" aria-hidden />
                </span>
              )}
            </span>,
            effectiveRate != null ? formatPerHour(effectiveRate) : '—',
            'text-sky-700',
          )}
          {cell(
            <span className="inline-flex items-center gap-0.5">
              Total expenses
              {expensesHint && (
                <span className="text-slate-400 cursor-help" title={expensesHint}>
                  <HelpCircle className="w-2.5 h-2.5 shrink-0" aria-hidden />
                </span>
              )}
            </span>,
            totalLaborCost != null ? formatMoney0(totalLaborCost) : '—',
            'text-slate-700',
          )}
          {cell(
            'Cost non-billable',
            costOfNonBillable != null ? formatMoney0(costOfNonBillable) : '—',
            'text-amber-700',
          )}
          {cell(
            'Profit / loss',
            profit != null ? formatMoney0(profit) : '—',
            profit != null ? (profit >= 0 ? 'text-emerald-700' : 'text-rose-600') : 'text-slate-400',
          )}
          {cell(
            'Margin / billable hr',
            marginPerBillableHour != null ? formatPerHour(marginPerBillableHour) : '—',
            'text-sky-700',
          )}
        </div>
        {!hasBilled && (
          <p className="text-[9px] text-slate-500 italic mt-2 leading-snug">
            Invoice billing not available; hours only.
          </p>
        )}
      </div>
    </div>
  );
};

export interface ClientDetailDepositCardProps {
  deposit: number;
  adjustment: number;
  balance: number;
  depositDetail?: string;
  adjustmentDetail?: string;
  balanceDetail?: string;
  title?: string;
}

export const ClientDetailDepositPositionCard: React.FC<ClientDetailDepositCardProps> = ({
  deposit,
  adjustment,
  balance,
  depositDetail,
  adjustmentDetail,
  balanceDetail,
  title = 'Deposit Position',
}) => {
  const balancePositive = balance >= 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex w-full h-full min-w-0 overflow-hidden">
      <div className={`w-1 flex-none self-stretch ${balancePositive ? 'bg-emerald-500' : 'bg-red-500'}`} />

      <div className="flex-1 min-w-0 px-3 py-2.5 flex flex-col gap-1.5">
        {/* Card title */}
        <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
          {title}
        </p>

        {/* Net Balance row */}
        <div className="flex items-center gap-2">
          <div>
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Net Balance</p>
            <p className={`text-base font-bold tabular-nums leading-tight ${balancePositive ? 'text-emerald-700' : 'text-red-600'}`}>
              {balanceDetail ?? fmtUSD(balance)}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 self-end mb-0.5
            ${balancePositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${balancePositive ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {balancePositive ? 'Positive' : 'Deficit'}
          </span>
        </div>

        {/* Deposit + Adjustment row */}
        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-medium">
          <span className="tabular-nums">
            Deposit: <span className="font-bold text-slate-800">{depositDetail ?? fmtUSD(deposit)}</span>
          </span>
          <span className={`w-1.5 h-1.5 rounded-full flex-none ${balancePositive ? 'bg-emerald-400' : 'bg-red-400'}`} />
          <span className="tabular-nums">
            Adjustment: <span className="font-bold text-slate-800">{adjustmentDetail ?? fmtUSD(adjustment)}</span>
          </span>
        </div>
      </div>
    </div>
  );
};
